import { Link } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { Shield, TrendingUp, ChevronRight, Activity, CheckCircle, X, Leaf, RefreshCw } from 'lucide-react'
import { GlassPanel, GlassButton, GlassBadge } from '../components/ui/glass'
import { Dialog, DialogContent, DialogClose } from '../components/ui/dialog'
import { apiUrl } from '../config/api'
import { type HomeFeature, HOW_IT_WORKS, FEATURES, ENTERPRISE_FEATURES, CHAIN_BASE } from './homeData'
import { MeasuredPlatformStats } from '../components/MeasuredPlatformStats'
import { useApiPolling } from '../hooks/useApiPolling'
import { formatMeasured } from '../lib/measured'
import { whaleVolumeValue, whaleVolumeSub } from '../lib/whaleVolume'
import { staleFlowNotice } from '../lib/flowAge'
import { fetchPlatformStats } from '../lib/platformStats'


function formatUSD(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(3)}B`
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  return `$${n.toLocaleString()}`
}

interface MevStatsData {
  totalVolumeUSD: number
  totalMevExposureUSD: number
  entityCount: number
  flowCount: number
  unpricedFlowCount?: number
  sources: string[]
  priceSource?: { eth: string; btc: string }
  topFlows: Array<{
    entity: string
    asset: string
    amountUSD: number
    mevExposureUSD: number
    signal: string
    direction: string
    timestamp: string
  }>
}

function useMevStats() {
  const [data, setData] = useState<MevStatsData | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastFetched, setLastFetched] = useState<Date | null>(null)

  const load = useCallback(async (force: boolean) => {
    if (force) setIsRefreshing(true)
    try {
      const res = await fetch(apiUrl(force ? '/api/mev-stats?refresh=1' : '/api/mev-stats'))
      if (!res.ok) return
      setData(await res.json())
      setLastFetched(new Date())
    } catch { /* non-fatal */ }
    finally { if (force) setIsRefreshing(false) }
  }, [])

  useEffect(() => {
    load(false)
    const interval = setInterval(() => load(false), 60_000)
    return () => clearInterval(interval)
  }, [load])

  return { data, isRefreshing, lastFetched, refresh: () => load(true) }
}

function HomeFeatureModal({ feature, onClose }: { feature: HomeFeature | null; onClose: () => void }) {
  if (!feature) return null
  const { icon: Icon, title, tag, learn } = feature
  return (
    <Dialog open={!!feature} onOpenChange={open => { if (!open) onClose() }}>
      <DialogContent className="!bg-[#0a0f1a]/95 !border-white/10 backdrop-blur-2xl !text-white max-w-2xl !p-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-4 p-6 border-b border-white/10">
          <div className="w-12 h-12 rounded-xl bg-gradient-brand flex items-center justify-center shrink-0 shadow-glow-sky">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-display text-xl font-bold text-white">{title}</h2>
              <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/20 border border-sky-400/30 text-sky-300">{tag}</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">{learn.overview}</p>
          </div>
          <DialogClose className="shrink-0 text-white/40 hover:text-white transition-colors mt-1">
            <X className="w-5 h-5" />
          </DialogClose>
        </div>
        {/* Body */}
        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* How it works */}
          <div>
            <h3 className="font-display text-sm font-bold text-white/70 uppercase tracking-wider mb-3">How It Works</h3>
            <ol className="space-y-3">
              {learn.howItWorks.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-sky-500/20 border border-sky-400/30 text-sky-300 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-white/70 text-sm leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </div>
          {/* Why it matters */}
          <div className="rounded-xl bg-sky-500/[0.06] border border-sky-400/20 p-4">
            <h3 className="font-display text-sm font-bold text-sky-300 mb-2">Why It Matters</h3>
            <p className="text-white/60 text-sm leading-relaxed">{learn.whyItMatters}</p>
          </div>
          {/* Tech stack */}
          <div>
            <h3 className="font-display text-sm font-bold text-white/70 uppercase tracking-wider mb-3">Tech Stack</h3>
            <div className="flex flex-wrap gap-2">
              {learn.techDetails.map(t => (
                <span key={t} className="text-xs px-3 py-1 rounded-full bg-white/[0.06] border border-white/10 text-white/60">{t}</span>
              ))}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
          <Link to="/features" onClick={onClose} className="text-sm text-white/50 hover:text-white transition-colors">
            View all features →
          </Link>
          <Link to="/pricing" onClick={onClose}>
            <GlassButton size="sm">View pricing →</GlassButton>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function HomePage() {
  const platform = useApiPolling(fetchPlatformStats, { intervalMs: null })
  const { data: mevStats, isRefreshing, refresh } = useMevStats()
  const flowAgeNotice = mevStats ? staleFlowNotice(mevStats.topFlows.map((f) => f.timestamp)) : null
  const [activeFeature, setActiveFeature] = useState<HomeFeature | null>(null)
  return (
    <div className="min-h-screen font-body text-white">
      {/* Hero */}
      <section className="min-h-screen flex items-center pt-16">
        <div className="max-w-7xl mx-auto px-4 w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-20">
          <div>
            <GlassBadge variant="info" className="mb-6">🛡️ MEV detection and analytics</GlassBadge>
            <p className="font-display text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
              See what MEV did to your trades.
            </p>
            <h1 className="font-display text-display-lg text-white leading-tight mb-6">
              Find Out If You Lost Money to{' '}
              <span className="bg-gradient-brand bg-clip-text text-transparent">MEV Attacks</span>
            </h1>
            {/* W7: this line used to promise "Threshold encryption, fair ordering, and real-time attack
                detection". None of the three is built; see docs/audit/W7_CAPABILITY_CLAIMS.md. */}
            <p className="text-white/60 text-lg leading-relaxed mb-8 max-w-lg">
              Sandwich detection for mined Ethereum transactions through the authenticated API, address checksum
              checks, and institutional flow data. Transaction protection is not available yet.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/signup">
                <GlassButton size="lg">Start Free →</GlassButton>
              </Link>
              <Link to="/features">
                <GlassButton variant="secondary" size="lg">View Features</GlassButton>
              </Link>
            </div>
            {/* Ticker */}
            <div className="mt-10">
              <MeasuredPlatformStats variant="inline" />
            </div>
          </div>

          {/* Right panel — animated shield visual */}
          <div className="relative flex justify-center">
            <div className="relative w-80 h-80">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/20 to-accent/10 blur-3xl animate-blob" />
              <GlassPanel className="absolute inset-8 rounded-full flex items-center justify-center" gradient>
                <div className="text-center">
                  <Shield className="w-16 h-16 text-primary mx-auto mb-3 drop-shadow-[0_0_20px_rgba(14,165,233,0.6)]" />
                  <div className="font-display text-2xl font-bold text-white">MEV Shield</div>
                </div>
              </GlassPanel>
              {/* Orbiting stats */}
              {/* W7: only sandwiches are detected (mev_detect.py NOT_COVERED lists frontrun, JIT, ...). */}
              {[
                { label: 'Sandwich', color: 'text-red-400', pos: 'top-4 right-4' },
              ].map(({ label, color, pos }) => (
                <div key={label} className={`absolute ${pos}`}>
                  <GlassPanel padding="sm" className="text-center min-w-[80px]">
                    <div className={`text-xs font-medium ${color}`}>{label}</div>
                  </GlassPanel>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Live Chain Savings Tracker */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <GlassBadge variant="success" className="mb-4">
              Recorded per chain
            </GlassBadge>
            <h2 className="font-display text-display-md text-white mb-3">
              What MEV Shield has <span className="bg-gradient-brand bg-clip-text text-transparent">recorded per chain</span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto text-sm">
              Counts read live from /api/stats. A chain MEV Shield records nothing for says so.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {CHAIN_BASE.map((chain) => {
              const counts = chain.statsKey ? platform.data?.chains?.[chain.statsKey] : undefined
              const loading = platform.loading && Boolean(chain.statsKey)
              return (
                <Link key={chain.id} to={`/chain/${chain.id}`} className="block group">
                  <GlassPanel
                    className={`border ${chain.borderColor} relative overflow-hidden hover:border-opacity-60 transition-all`}
                    padding="lg"
                  >
                    {/* Background gradient blob */}
                    <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gradient-radial ${chain.gradientFrom} to-transparent opacity-40 blur-2xl pointer-events-none`} />

                    {/* Chain header */}
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-xl ${chain.bgColor} border ${chain.borderColor} flex items-center justify-center font-display text-xs font-bold ${chain.color}`}>
                          {chain.symbol.slice(0, 3)}
                        </div>
                        <div>
                          <div className="font-display text-sm font-bold text-white">{chain.name}</div>
                          <div className="text-white/30 text-xs">{chain.symbol}</div>
                        </div>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-white/20 group-hover:text-white/50 transition-colors" />
                    </div>

                    {/* /api/stats counts only */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-white/30 text-xs mb-0.5">Encrypted transactions recorded</div>
                        <div className="font-display text-base font-bold text-white tabular-nums" data-testid={`home-protected-${chain.id}`}>
                          {formatMeasured(counts?.protected, { loading })}
                        </div>
                      </div>
                      <div>
                        <div className="text-white/30 text-xs mb-0.5">Threats blocked (all time)</div>
                        <div className={`font-display text-base font-bold ${chain.color} tabular-nums`} data-testid={`home-threats-${chain.id}`}>
                          {formatMeasured(counts?.threats, { loading })}
                        </div>
                      </div>
                    </div>

                    <div className={`mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-between`}>
                      <span className="text-xs text-white/30">View chain details</span>
                      <ChevronRight className="w-3 h-3 text-white/20 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </GlassPanel>
                </Link>
              )
            })}
          </div>

        </div>
      </section>

      {/* Institutional whale flows (S9.2 W9-08: no recency wording; old rows say how old) */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-4">
              <GlassBadge variant="info">
                On-chain flow data
              </GlassBadge>
              <button
                type="button"
                onClick={refresh}
                disabled={isRefreshing}
                aria-label="Refresh institutional flows"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-white/50 hover:text-white/90 hover:bg-white/5 border border-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
            <h2 className="font-display text-display-md text-white mb-3">
              Institutional{' '}
              <span className="bg-gradient-brand bg-clip-text text-transparent">Whale Flows</span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto text-sm">
              Large on-chain flows from tracked institutional wallets, read from /api/mev-stats.
            </p>
          </div>

          {/* Summary stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {[
              {
                label: 'Whale Volume Tracked',
                value: whaleVolumeValue(mevStats, formatUSD),
                sub: whaleVolumeSub(mevStats),
                color: 'text-sky-400',
                icon: TrendingUp,
              },
              {
                label: 'Entities Monitored',
                value: mevStats ? String(mevStats.entityCount) : '—',
                sub:
                  mevStats != null && mevStats.sources.length > 0
                    ? `Sources: ${mevStats.sources.join(', ')}`
                    : mevStats != null
                      ? 'No rows from any source'
                      : '—',
                color: 'text-purple-400',
                icon: Activity,
              },
            ].map(({ label, value, sub, color, icon: Icon }) => (
              <GlassPanel key={label} className="border border-white/5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
                    <Icon className={`w-5 h-5 ${color}`} />
                  </div>
                  <div>
                    <div className="text-white/40 text-xs font-medium uppercase tracking-wider mb-1">{label}</div>
                    <div className={`font-display text-2xl font-bold ${color} tabular-nums`}>{value}</div>
                    <div className="text-white/30 text-xs mt-0.5">{sub}</div>
                  </div>
                </div>
              </GlassPanel>
            ))}
          </div>

          {/* Top flows table — Without vs With MEV Shield */}
          {mevStats && mevStats.topFlows.length > 0 && (
            <GlassPanel className="border border-white/5 overflow-hidden">
              <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
                <div>
                  <h3 className="font-display text-sm font-bold text-white">Top Institutional Flows</h3>
                  <p className="text-white/40 text-xs mt-0.5">Largest tracked flows by USD value</p>
                  {flowAgeNotice && (
                    <p data-testid="home-flow-age-notice" className="text-amber-300/80 text-xs mt-1">{flowAgeNotice}</p>
                  )}
                </div>
                <Link to="/compare" className="text-xs text-sky-400 hover:text-sky-300 transition-colors">
                  Full comparison →
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th scope="col" className="text-left px-6 py-3 text-white/30 text-xs font-medium uppercase tracking-wider">Entity</th>
                      <th scope="col" className="text-right px-6 py-3 text-white/30 text-xs font-medium uppercase tracking-wider">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mevStats.topFlows.map((flow, i) => (
                      // Not keyed by the age label: two rows can share one ("591d ago").
                      <tr key={`${i}-${flow.entity}-${flow.asset}-${flow.amountUSD}`} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                        <td className="px-6 py-3">
                          <div className="font-medium text-white/80 text-xs leading-tight max-w-[200px] truncate">{flow.entity}</div>
                          <div className="text-white/30 text-[10px] mt-0.5">{flow.asset} · {flow.direction} · {flow.timestamp}</div>
                        </td>
                        <td className="px-6 py-3 text-right font-display font-bold text-white tabular-nums">
                          {formatUSD(flow.amountUSD)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlassPanel>
          )}
        </div>
      </section>

      {/* Sustainability Banner */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="rounded-2xl bg-emerald-400/5 border border-emerald-500/10 p-8 lg:p-12">
            <div className="grid grid-cols-1 gap-10 items-center">
              {/* Left column */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Leaf className="w-5 h-5 text-emerald-400" />
                  <GlassBadge variant="success">Sustainability</GlassBadge>
                </div>
                <h2 className="font-display text-display-sm text-white mb-4">
                  Sustainability-First DeFi Protection
                </h2>
                <p className="text-white/60 text-base leading-relaxed mb-2">
                  CCRI estimates that The Merge (September 2022) cut Ethereum&apos;s annualized electricity consumption by more than 99.988% (
                  <a href="https://ethereum.org/en/energy-consumption/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">ethereum.org, Ethereum&apos;s energy consumption</a>
                  ). MEV Shield does not yet measure carbon offsets for your swaps.
                </p>
                <Link to="/dashboard?tab=carbon-report">
                  <GlassButton className="mt-4">View Carbon Report →</GlassButton>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="font-display text-display-md text-white mb-4">How It Works</h2>
            <p className="text-white/50 max-w-xl mx-auto">What works today, and the step that is not available yet.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map(({ step, title, desc }) => (
              <Link key={step} to="/docs" className="block group">
                <GlassPanel gradient hover padding="lg" className="relative overflow-hidden h-full">
                  <div className="font-display text-6xl font-bold text-white/[0.04] absolute -top-2 -right-2">{step}</div>
                  <div className="font-display text-sm text-primary font-bold mb-3 tracking-wider">{step}</div>
                  <h3 className="font-display text-xl font-bold text-white mb-2">{title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-4">{desc}</p>
                  <div className="flex items-center gap-1 text-xs text-primary/60 group-hover:text-primary transition-colors">
                    Read the docs <ChevronRight className="w-3 h-3" />
                  </div>
                </GlassPanel>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="font-display text-display-md text-white mb-4">What MEV Shield Does Today</h2>
            <p className="text-white/50 max-w-xl mx-auto">Detection, address checks, flow data and the platform behind them. Features that are not built are not listed.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURES.map((feature, idx) => {
              const isHero = idx === 0
              const { icon: Icon, title, desc, tag, blogSlug } = feature
              return (
                <div key={title} className="block group cursor-pointer" onClick={() => setActiveFeature(feature)}>
                  <GlassPanel
                    gradient
                    hover
                    padding="lg"
                    className={`h-full ${isHero ? 'border border-sky-500/20 bg-sky-500/[0.03] shadow-[0_0_30px_rgba(14,165,233,0.05)]' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`${isHero ? 'w-14 h-14' : 'w-12 h-12'} rounded-xl bg-gradient-brand flex items-center justify-center shrink-0 ${isHero ? 'shadow-[0_0_20px_rgba(14,165,233,0.3)]' : 'shadow-glow-sky'}`}>
                        <Icon className={`${isHero ? 'w-7 h-7' : 'w-6 h-6'} text-white`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className={`font-display ${isHero ? 'text-xl' : 'text-lg'} font-bold text-white`}>{title}</h3>
                          <GlassBadge variant="info">{tag}</GlassBadge>
                          {isHero && (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-sky-400 bg-sky-500/10 border border-sky-500/20 px-2 py-0.5 rounded-full">★ Core</span>
                          )}
                        </div>
                        <p className={`${isHero ? 'text-white/60' : 'text-white/50'} text-sm leading-relaxed mb-3`}>{desc}</p>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1 text-xs text-primary/60 group-hover:text-primary transition-colors">
                            Learn more <ChevronRight className="w-3 h-3" />
                          </div>
                          {blogSlug && (
                            <Link
                              to={`/blog/${blogSlug}`}
                              onClick={e => e.stopPropagation()}
                              className="flex items-center gap-1 text-xs text-accent/60 hover:text-accent transition-colors"
                            >
                              Read blog <ChevronRight className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </GlassPanel>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Metrics Band */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <MeasuredPlatformStats />
        </div>
      </section>

      {/* Enterprise */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <GlassPanel gradient padding="lg" className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-brand opacity-5 blur-3xl rounded-full" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <GlassBadge variant="success" className="mb-4">Enterprise</GlassBadge>
                <h2 className="font-display text-display-sm text-white mb-4">
                  Built for institutional-grade DeFi
                </h2>
                <p className="text-white/50 leading-relaxed mb-6">
                  What an organisation gets today: the admin approvals queue, liquidity analytics, detection and flow data.
                </p>
                <Link to="/pricing">
                  <GlassButton>View Pricing <ChevronRight className="w-4 h-4 ml-1" /></GlassButton>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ENTERPRISE_FEATURES.map(feat => (
                  <div key={feat} className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle className="w-4 h-4 text-accent shrink-0" />
                    {feat}
                  </div>
                ))}
              </div>
            </div>
          </GlassPanel>
        </div>
      </section>

      {/* Latest Insights */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-12">
            <div>
              <GlassBadge variant="info" className="mb-3">Blog</GlassBadge>
              <h2 className="font-display text-display-sm text-white">Latest Insights</h2>
            </div>
            <Link to="/blog" className="text-sm text-primary/60 hover:text-primary transition-colors flex items-center gap-1">
              All posts <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { slug: 'address-poisoning-protection', title: 'Address Poisoning: The Silent Wallet Attack Draining DeFi Users', excerpt: 'How lookalike wallet addresses steal funds, and what an address checksum check does and does not catch.', tag: 'Security', date: 'Mar 21, 2026', readTime: '7 min' },
              { slug: 'mev-insurance', title: 'MEV Insurance (corrected 2026-09-29: not available)', excerpt: 'Correction 2026-09-29: MEV insurance is not available. Coverage tiers, premiums and pricing previously published here have been removed.', tag: 'Protection', date: 'Mar 19, 2026', readTime: '1 min' },
            ].map(post => (
              <Link key={post.slug} to={`/blog/${post.slug}`}>
                <GlassPanel hover padding="lg" className="h-full flex flex-col">
                  <div className="h-28 rounded-xl bg-gradient-to-br from-primary/20 to-accent/10 mb-4 flex items-center justify-center">
                    <span className="font-display text-3xl font-bold text-white/10">{post.tag[0]}</span>
                  </div>
                  <GlassBadge variant="info" className="mb-3 self-start">{post.tag}</GlassBadge>
                  <h3 className="font-display text-base font-bold text-white mb-2 leading-tight flex-1">{post.title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-white/30">
                    <span>{post.date}</span>
                    <span>{post.readTime} read</span>
                  </div>
                </GlassPanel>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-brand opacity-10 blur-3xl rounded-full" />
            <GlassPanel gradient padding="lg">
              <h2 className="font-display text-display-sm text-white mb-4">
                Stop losing money to MEV.{' '}
                <span className="bg-gradient-brand bg-clip-text text-transparent">Start free today.</span>
              </h2>
              <p className="text-white/50 mb-8">No credit card required. Sign up in minutes.</p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/signup">
                  <GlassButton size="lg">Get Started Free →</GlassButton>
                </Link>
                <Link to="/contact">
                  <GlassButton variant="secondary" size="lg">Talk to Sales</GlassButton>
                </Link>
              </div>
            </GlassPanel>
          </div>
        </div>
      </section>

      <HomeFeatureModal feature={activeFeature} onClose={() => setActiveFeature(null)} />
    </div>
  )
}
